import { useEffect, useState } from "react";
import { ResponsiveRow } from "../constants/layout.constants";
import Permit from "../util/permit";
import {
  formatCurrency,
  formatDate,
  isEmptyString,
  isNullUndefined,
} from "../util/util";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { History, Visibility } from "@mui/icons-material";
import MuiDataGrid from "../components/modules/MuiDataGrid";
import { listPettyCashEntries } from "../services/pettyCash.service";
import {
  TopBarContainedBrandButton,
  TopBarContainer,
} from "../constants/component.constants";
import { Typography } from "@mui/material";
import PettyCashTopup from "../components/modals/accounting/petty cash/pettyCashTopup";
import ViewPettyCash from "../components/modals/accounting/petty cash/viewPettyCash";
import ReverseTopupModal from "../components/modals/accounting/petty cash/reverseTopup";
import GenerateReport from "../components/modals/generateReport";

const PettyCash = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [add, setAdd] = useState(false);
  const [view, setView] = useState(false);
  const [reverse, setReverse] = useState(false);
  const [showGenerateReport, setShowGenerateReport] = useState(false);

  useEffect(() => {
    setLoading(true);
    listPettyCashEntries()
      .then((data) => {
        setTransactions(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!add || !reverse) {
      setLoading(true);
      listPettyCashEntries()
        .then((data) => {
          setTransactions(data);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [add, reverse]);

  const dataGridColumns = [
    { field: "description", headerName: "Description", minWidth: 400, flex: 5 },
    {
      field: "postingDate",
      headerName: "Date",
      minWidth: 150,
      flex: 1.2,
      valueFormatter: (row) => {
        return formatDate(row.value);
      },
    },
    {
      field: "bankAccount",
      headerName: "Bank Account",
      minWidth: 200,
      flex: 3.5,
    },
    {
      field: "ledgerAccount",
      headerName: "Ledger Account",
      minWidth: 200,
      flex: 3.5,
    },
    { field: "appliedTo", headerName: "Applied To", minWidth: 200, flex: 3.5 },
    {
      field: "createdBy",
      headerName: "Created By",
      minWidth: 200,
      flex: 2.75,
      valueFormatter: (row) => {
        if (isNullUndefined(row.value) || isEmptyString(row.value)) {
          return "System Generated";
        } else {
          return row.value;
        }
      },
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      minWidth: 110,
      flex: 1.1,
      valueFormatter: (row) => {
        return formatCurrency(row.value);
      },
    },
    {
      field: "action",
      headerName: "Actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        const row = params.row;
        const buttons = [
          <GridActionsCellItem
            icon={<Visibility />}
            label="View"
            onClick={() => handleViewClick(params.row)}
            showInMenu
          />,
        ]
        
        if(row?.description.includes("Reversed")) {
          return buttons
        } else {
          buttons.push(
            <GridActionsCellItem
              icon={<History />}
              label="Reverse Top-up"
              onClick={() => handleReverseClick(params.row)}
              showInMenu
            />
          )
          return buttons
        }
      },
    },
  ];

  const getRowClassName = (params) => {
    const description = params.row?.description || "";
    if (description.includes("Reversed")) {
      return "voided";
    }
    return "";
  };

  const handleViewClick = (journalEntry) => {
    setSelectedTransaction(journalEntry);
    setView(true);
  };

  const handleReverseClick = (journalEntry) => {
    setSelectedTransaction(journalEntry);
    setReverse(true);
  };

  const handleAddClick = () => {
    setAdd(true);
  };

  const handlePrintReport = () => {
    setShowGenerateReport(true);
  };

  return (
    <Permit
      roles="BLDIR,ACC,BLDMG,CS"
      services="PREMIUM,HOA,MAINT"
      noPermittedServiceMessage={{
        title: "Access Denied",
        body: (
          <>
            Your current role and/or facility is not permitted to view this
            page. <br /> Please contact your system admin if you feel this is an
            error.
          </>
        ),
      }}
    >
      <ResponsiveRow
        sx={{
          width: "100%",
          height: "auto",
          flexWrap: "nowrap",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          padding: 0,
        }}
      >
        <TopBarContainer
          sx={{
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            width: { xs: "100%", sm: "80%" },
            marginTop: "2vh",
            marginBottom: "2vh",
          }}
          container
        >
          <Typography
            variant="h5"
            sx={{
              width: { xs: "100%", sm: "25%" },
              height: "auto",
              textAlign: { xs: "center", sm: "start" },
            }}
          >
            Petty Cash Management
          </Typography>

          <ResponsiveRow
            item
            sx={{
              width: { xs: "100%", sm: "75%" },
              padding: 0,
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-end" },
            }}
            container
          >
            <TopBarContainedBrandButton
              sx={{ width: { xs: "100%", sm: "200px" } }}
              onClick={handleAddClick}
            >
              Top-up Petty Cash
            </TopBarContainedBrandButton>
            <TopBarContainedBrandButton
              sx={{ width: { xs: "100%", sm: "200px" } }}
              onClick={handlePrintReport}
            >
              Petty Cash Book Report
            </TopBarContainedBrandButton>
          </ResponsiveRow>
        </TopBarContainer>
        <ReverseTopupModal
          open={reverse ?? false}
          close={setReverse}
          topUp={selectedTransaction}
        />
        <PettyCashTopup open={add ?? false} close={setAdd} />
        <ViewPettyCash
          open={view ?? false}
          close={() => {
            setView(false);
          }}
          pettyCash={selectedTransaction}
        />
        <MuiDataGrid
          dataGridColumns={dataGridColumns}
          noSelection={true}
          dataGridRows={transactions}
          loading={loading}
          height="60vh"
          totalRows={transactions.length}
          getRowClassName={getRowClassName}
        />
        <GenerateReport
            showModal={showGenerateReport}
            closeModal={() => setShowGenerateReport(false)}
            report={"Petty Cash"}
            description={"A report that captures and includes all transactions made using petty cash account."}
        />
      </ResponsiveRow>
    </Permit>
  );
};

export default PettyCash;
